import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Modal, Form, Input, Space } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import AdminNavbar from '../components/AdminNavbar';
import { useNavigate } from 'react-router-dom';


const AdminCenters = () => {
  const [centers, setCenters] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCenters();
  }, []);

  const getAuthHeaders = () => {
    const authToken = localStorage.getItem("authToken");
    return {
      Authorization: `Bearer ${authToken}`,
      "X-CSRFToken": getCSRFToken(),
    };
  };

  function getCSRFToken() {
    return window.CSRF_TOKEN || '';
  }

  const fetchCenters = async () => {
    try {
      const response = await axios.get("/api/centers", {
        headers: getAuthHeaders(),
      });
      setCenters(response.data);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const handleAdd = () => {
    setEditingCenter(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCenter(record);
    form.setFieldsValue({
      ...record,
      employees: record.employees || [],
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/centers/${id}`, {
        headers: getAuthHeaders(),
      });
      fetchCenters();
    } catch (error) {
      console.error("Error deleting center:", error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCenter) {
        await axios.put(`/api/centers/${editingCenter.id}`, values, {
          headers: getAuthHeaders(),
        });
      } else {
        await axios.post("/api/centers", values, {
          headers: getAuthHeaders(),
        });
      }
      setIsModalVisible(false);
      fetchCenters();
    } catch (error) {
      console.error("Error saving center:", error);
    }
  };

  const columns = [
    {
      title: 'District',
      dataIndex: 'district',
      key: 'district',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (

    <>

    <AdminNavbar/>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <Button
    onClick={() => navigate('/admin-dashboard')}
    className="bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded-md text-sm mb-4"
    >
    Back to Dashboard
    </Button>

    <div className="text-2xl font-bold text-blue-800 mb-6">Centers Management</div>

      <Card
        title={
          <div className="flex justify-end items-center">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Center
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <Table
          columns={columns}
          dataSource={centers}
          rowKey="id"
          expandable={{
            expandedRowRender: (record) => (
              <Table
                dataSource={record.employees}
                columns={[
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Designation', dataIndex: 'designation' },
                  { title: 'Contact', dataIndex: 'contact_number' },
                ]}
                pagination={false}
                rowKey="id"
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingCenter ? 'Edit Center' : 'Add Center'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          className="pt-4"
        >
          <Form.Item
            name="district"
            label="District"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Center Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="map_url"
            label="Map URL"
          >
            <Input />
          </Form.Item>

          <Form.List name="employees">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} className="mb-4" size="small">
                    <div className="flex justify-end mb-2">
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </div>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      label="Employee Name"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'designation']}
                      label="Designation"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'contact_number']}
                      label="Contact Number"
                    >
                      <Input />
                    </Form.Item>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Employee
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item className="mb-0">
            <div className="flex justify-end">
              <Space>
                <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit">
                  {editingCenter ? 'Update' : 'Create'}
                </Button>
              </Space>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </>
  );
};

export default AdminCenters;